import { useState, useEffect } from 'react';
import { dashboardApi, filesApi, insightsApi, budgetsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GlobalFilters } from '../components/GlobalFilters';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Target, 
  FileSpreadsheet,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Filter,
  CheckCircle2
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';

const formatCurrency = (value) => {
  if (Math.abs(value) >= 1000000) {
    return `R ${(value / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `R ${(value / 1000).toFixed(0)}K`;
  }
  return `R ${value.toFixed(0)}`;
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [variance, setVariance] = useState(null);
  const [selectedBu, setSelectedBu] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('2025/2026'); // Default to current FY
  const [selectedStatus, setSelectedStatus] = useState('all'); // Default to all statuses
  const [fiscalYears, setFiscalYears] = useState([]);
  const { isDark } = useTheme();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  // Load fiscal years on mount
  useEffect(() => {
    const loadFiscalYears = async () => {
      try {
        const res = await budgetsApi.getFiscalYears();
        setFiscalYears(res.data || []);
      } catch (error) {
        console.error('Failed to load fiscal years');
      }
    };
    loadFiscalYears();
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [selectedBu, selectedPeriod, selectedFiscalYear, selectedStatus]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedBu) params.business_unit_id = selectedBu;
      if (selectedPeriod) params.period = selectedPeriod;
      if (selectedFiscalYear && selectedFiscalYear !== 'all') params.fiscal_year = selectedFiscalYear;
      if (selectedStatus && selectedStatus !== 'all') params.status = selectedStatus;
      
      const [summaryRes, cashflowRes, varianceRes] = await Promise.all([
        dashboardApi.getSummary(params),
        dashboardApi.getCashflow(params),
        dashboardApi.getVariance(params)
      ]);
      setSummary(summaryRes.data);
      setCashflow(cashflowRes.data);
      setVariance(varianceRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const chartData = cashflow ? cashflow.months.map((month, i) => ({
    month,
    forecasted: cashflow.forecasted[i] / 1000000,
    actual: cashflow.actual[i] / 1000000,
  })) : [];

  const kpis = summary?.kpis || {
    total_revenue: 0,
    total_expenses: 0,
    net_income: 0,
    forecast_accuracy: 0,
    budget_variance: 0
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32 rounded-sm" />
          ))}
        </div>
        <Skeleton className="h-80 rounded-sm" />
      </div>
    );
  }

  return (
    <div className={`p-6 space-y-6 ${isDark ? 'text-white' : ''}`} data-testid="dashboard-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className={`font-display text-3xl font-light ${isDark ? 'text-white' : 'text-slate-900'}`}>Dashboard</h1>
          <p className={isDark ? 'text-zinc-400 mt-1' : 'text-slate-500 mt-1'}>
            {selectedFiscalYear && selectedFiscalYear !== 'all' ? `FY ${selectedFiscalYear}` : 'All Years'} 
            {' '}{kpis.ytd_month ? `• YTD to ${kpis.ytd_month}` : ''}
            {selectedStatus === 'approved' && ' • Approved'}
            {selectedStatus === 'pending' && ' • Pending'}
            {selectedStatus === 'draft' && ' • Drafts'}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Fiscal Year Filter */}
          <Select value={selectedFiscalYear || 'all'} onValueChange={setSelectedFiscalYear}>
            <SelectTrigger 
              className={`w-[140px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
              data-testid="fiscal-year-filter"
            >
              <SelectValue placeholder="Fiscal Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {fiscalYears.map(fy => (
                <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select value={selectedStatus || 'all'} onValueChange={setSelectedStatus}>
            <SelectTrigger 
              className={`w-[150px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
              data-testid="status-filter"
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="approved">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  Approved
                </div>
              </SelectItem>
              <SelectItem value="pending">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  Pending
                </div>
              </SelectItem>
              <SelectItem value="draft">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-slate-500" />
                  Draft
                </div>
              </SelectItem>
            </SelectContent>
          </Select>

          <GlobalFilters
            selectedBu={selectedBu}
            selectedPeriod={selectedPeriod}
            onBuChange={setSelectedBu}
            onPeriodChange={setSelectedPeriod}
            showPeriodFilter={false}
          />
        </div>
      </div>

      {/* KPI Cards - Showing Budget, Actual, and Variance */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Revenue Card */}
        <Card 
          className={`border border-slate-200 shadow-none rounded-sm cursor-pointer transition-all hover:shadow-md hover:border-emerald-300 ${isDark ? 'bg-zinc-900 border-zinc-800 hover:border-emerald-700' : ''}`}
          onClick={() => navigate('/budgets')}
          data-testid="kpi-revenue"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-9 h-9 ${isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'} rounded-sm flex items-center justify-center`}>
                <DollarSign className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded ${isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-100 text-slate-500'}`}>
                Revenue
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>YTD Budget</span>
                <span className={`font-mono text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {formatCurrency(kpis.total_revenue_budget)}
                </span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>YTD Actual</span>
                <span className={`font-mono text-lg ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                  {formatCurrency(kpis.total_revenue)}
                </span>
              </div>
              <div className={`flex justify-between items-baseline pt-2 border-t ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Variance</span>
                <span className={`font-mono text-sm font-medium ${(kpis.total_revenue - kpis.total_revenue_budget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {(kpis.total_revenue - kpis.total_revenue_budget) >= 0 ? '+' : ''}{formatCurrency(kpis.total_revenue - kpis.total_revenue_budget)}
                  <span className="text-xs ml-1">
                    ({kpis.total_revenue_budget ? (((kpis.total_revenue - kpis.total_revenue_budget) / kpis.total_revenue_budget) * 100).toFixed(1) : 0}%)
                  </span>
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expenses Card */}
        <Card 
          className={`border border-slate-200 shadow-none rounded-sm cursor-pointer transition-all hover:shadow-md hover:border-red-300 ${isDark ? 'bg-zinc-900 border-zinc-800 hover:border-red-700' : ''}`}
          onClick={() => navigate('/budget')}
          data-testid="kpi-expenses"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-9 h-9 ${isDark ? 'bg-red-900/30' : 'bg-red-50'} rounded-sm flex items-center justify-center`}>
                <TrendingDown className="w-4 h-4 text-red-600" strokeWidth={1.5} />
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded ${isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-100 text-slate-500'}`}>
                Expenses
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>YTD Budget</span>
                <span className={`font-mono text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {formatCurrency(kpis.total_expenses_budget)}
                </span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>YTD Actual</span>
                <span className={`font-mono text-lg ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                  {formatCurrency(kpis.total_expenses)}
                </span>
              </div>
              <div className={`flex justify-between items-baseline pt-2 border-t ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Variance</span>
                <span className={`font-mono text-sm font-medium ${(kpis.total_expenses - kpis.total_expenses_budget) <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {(kpis.total_expenses - kpis.total_expenses_budget) >= 0 ? '+' : ''}{formatCurrency(kpis.total_expenses - kpis.total_expenses_budget)}
                  <span className="text-xs ml-1">
                    ({kpis.total_expenses_budget ? (((kpis.total_expenses - kpis.total_expenses_budget) / kpis.total_expenses_budget) * 100).toFixed(1) : 0}%)
                  </span>
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Net Income Card */}
        <Card 
          className={`border border-slate-200 shadow-none rounded-sm cursor-pointer transition-all hover:shadow-md hover:border-emerald-300 ${isDark ? 'bg-zinc-900 border-zinc-800 hover:border-emerald-700' : ''}`}
          onClick={() => navigate('/cashflow')}
          data-testid="kpi-net-income"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-9 h-9 ${isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'} rounded-sm flex items-center justify-center`}>
                <TrendingUp className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded ${isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-100 text-slate-500'}`}>
                Net Income
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>YTD Budget</span>
                <span className={`font-mono text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {formatCurrency(kpis.net_income_budget)}
                </span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>YTD Actual</span>
                <span className={`font-mono text-lg ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                  {formatCurrency(kpis.net_income)}
                </span>
              </div>
              <div className={`flex justify-between items-baseline pt-2 border-t ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Variance</span>
                <span className={`font-mono text-sm font-medium ${(kpis.net_income - kpis.net_income_budget) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {(kpis.net_income - kpis.net_income_budget) >= 0 ? '+' : ''}{formatCurrency(kpis.net_income - kpis.net_income_budget)}
                  <span className="text-xs ml-1">
                    ({kpis.net_income_budget ? (((kpis.net_income - kpis.net_income_budget) / kpis.net_income_budget) * 100).toFixed(1) : 0}%)
                  </span>
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Budgets Count Card */}
        <Card 
          className={`border border-slate-200 shadow-none rounded-sm cursor-pointer transition-all hover:shadow-md hover:border-violet-300 ${isDark ? 'bg-zinc-900 border-zinc-800 hover:border-violet-700' : ''}`}
          onClick={() => navigate('/budgets')}
          data-testid="kpi-accuracy"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-9 h-9 ${isDark ? 'bg-violet-900/30' : 'bg-violet-50'} rounded-sm flex items-center justify-center`}>
                <Target className="w-4 h-4 text-violet-600" strokeWidth={1.5} />
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded ${isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-100 text-slate-500'}`}>
                Summary
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Budgets</span>
                <span className={`font-mono text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {kpis.budgets_count}
                </span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Variance %</span>
                <span className={`font-mono text-lg ${kpis.budget_variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {kpis.budget_variance > 0 ? '+' : ''}{kpis.budget_variance}%
                </span>
              </div>
              <div className={`flex justify-between items-baseline pt-2 border-t ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Status</span>
                <span className={`text-xs font-medium ${kpis.is_consolidated ? 'text-violet-600' : 'text-amber-600'}`}>
                  {kpis.is_consolidated ? 'Consolidated' : 'Single BU'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Cashflow Chart - Main */}
        <Card className="lg:col-span-2 border border-slate-200 shadow-none rounded-sm" data-testid="cashflow-chart">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="font-display text-lg font-medium flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
              Cashflow Trends
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12, fill: '#6B7280' }}
                    axisLine={{ stroke: '#E5E7EB' }}
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#6B7280' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}M`}
                  />
                  <Tooltip 
                    formatter={(value) => [`R ${value.toFixed(1)}M`, '']}
                    contentStyle={{ 
                      borderRadius: '2px', 
                      border: '1px solid #E5E7EB',
                      boxShadow: 'none'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="forecasted" 
                    stroke="#6B7280" 
                    strokeWidth={2}
                    dot={false}
                    name="Forecasted"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="actual" 
                    stroke="#059669" 
                    strokeWidth={2}
                    dot={false}
                    name="Actual"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-slate-500 rounded-full"></div>
                <span className="text-slate-600">Forecasted</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-emerald-600 rounded-full"></div>
                <span className="text-slate-600">Actual</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Variance Summary */}
        <Card className="border border-slate-200 shadow-none rounded-sm" data-testid="variance-summary">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="font-display text-lg font-medium flex items-center gap-2">
              <Target className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
              Top Variances
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-3">
              {variance?.items?.slice(0, 5).map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{item.account}</p>
                    <p className="text-xs text-slate-500 font-mono">
                      Budget: {formatCurrency(item.budget)}
                    </p>
                  </div>
                  <div className={`px-2 py-1 rounded-sm text-xs font-mono font-medium ${
                    item.variance_pct >= 0 
                      ? 'bg-emerald-50 text-emerald-700' 
                      : 'bg-red-50 text-red-700'
                  }`}>
                    {item.variance_pct >= 0 ? '+' : ''}{item.variance_pct}%
                  </div>
                </div>
              ))}
            </div>
            <Button 
              variant="ghost" 
              className="w-full mt-4 text-sm text-slate-600 hover:text-slate-900"
              onClick={() => navigate('/budget')}
              data-testid="view-all-variances"
            >
              View all variances
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Files & AI Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Files */}
        <Card className="border border-slate-200 shadow-none rounded-sm" data-testid="recent-files">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="font-display text-lg font-medium flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
              Recent Uploads
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {summary?.recent_files?.length > 0 ? (
              <div className="space-y-2">
                {summary.recent_files.map((file, i) => (
                  <div 
                    key={i} 
                    className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-sm cursor-pointer transition-colors"
                    onClick={() => navigate(`/files/${file.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-emerald-50 rounded-sm flex items-center justify-center">
                        <FileSpreadsheet className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900 truncate max-w-[200px]">{file.filename}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(file.uploaded_at)}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded-sm">
                      {file.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-slate-300" strokeWidth={1.5} />
                <p>No files uploaded yet</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => navigate('/import-export')}
                >
                  Go to Import & Export
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Insights Preview */}
        <Card className="border border-violet-100 bg-violet-50/30 shadow-none rounded-sm" data-testid="ai-insights-preview">
          <CardHeader className="border-b border-violet-100 pb-4">
            <CardTitle className="font-display text-lg font-medium flex items-center gap-2 text-violet-900">
              <Sparkles className="w-5 h-5 text-violet-600" strokeWidth={1.5} />
              AI Insights
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="p-3 bg-white border border-violet-100 rounded-sm">
                <p className="text-sm font-medium text-violet-900">Revenue Growth Trend</p>
                <p className="text-xs text-slate-600 mt-1">
                  Rental income shows consistent growth of 8.2% YoY, driven primarily by student housing segment.
                </p>
              </div>
              <div className="p-3 bg-white border border-violet-100 rounded-sm">
                <p className="text-sm font-medium text-violet-900">Cashflow Alert</p>
                <p className="text-xs text-slate-600 mt-1">
                  December-April shows negative cashflow pattern. Consider reserve optimization.
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4 border-violet-200 text-violet-700 hover:bg-violet-50"
              onClick={() => navigate('/insights')}
              data-testid="view-all-insights"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              View All Insights
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
